import { useEffect, useMemo, useState } from "react";
import { DENSITY_HEIGHT } from "@/components/schedule/grid/rowUtils";

/**
 * Row virtualizer for the schedule grid.
 *
 * The grid is a CSS-Grid with sticky left columns and per-row CSS-Grid cells
 * emitted directly into the parent container. We can't swap to react-window
 * without destroying that layout — so instead we:
 *
 *   1. Compute the pixel height of every row up front (cheap; O(n) on small ints).
 *   2. Track the window scroll position + the grid element's offset.
 *   3. Return [visibleStart, visibleEnd] indices and two spacer pixel heights
 *      (top / bottom) that ScheduleGrid renders as `grid-column: 1 / -1`
 *      placeholders to reserve scroll space.
 *
 * The schedule layout uses `min-h-screen` (window-level scroll), so the grid's
 * `overflow-auto` never actually engages — the WINDOW is the scroll container.
 * We measure the grid's `getBoundingClientRect().top` to map window-scroll
 * coordinates into row-index space.
 *
 * Sticky columns + drag handles continue to work because rows inside the
 * window still emit their original Fragment of cells into the grid.
 *
 * Activates only when rows.length > THRESHOLD so small projects pay nothing.
 */
const THRESHOLD = 200;       // rows; below this we render everything
const OVERSCAN = 12;         // extra rows above + below the viewport
const DATE_H = 22;
const ROOM_H = 28;
const DATE_SUB_H = 20;
const FLOATING_DIVIDER_H = 20;
const NEWTASK_H = 28;

export function useVirtualRows({ rows, density, scrollRef }) {
  const rowH = DENSITY_HEIGHT[density] || 38;
  const subH = Math.max(24, rowH - 8);

  // Build the heights array. Cheap; rows are small ints.
  const heights = useMemo(() => {
    const out = new Array(rows.length);
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r.kind === "date") out[i] = DATE_H;
      else if (r.kind === "room") out[i] = ROOM_H;
      else if (r.kind === "date-sub") out[i] = DATE_SUB_H;
      else if (r.kind === "floating-divider") out[i] = FLOATING_DIVIDER_H;
      else if (r.kind === "newtask") out[i] = NEWTASK_H;
      else if (r.kind === "subtask") out[i] = subH;
      else out[i] = rowH;  // block + floating-task
    }
    return out;
  }, [rows, rowH, subH]);

  // Cumulative offsets — Y position above row i. Same length, prefix-sum style.
  const offsets = useMemo(() => {
    const out = new Array(heights.length + 1);
    out[0] = 0;
    for (let i = 0; i < heights.length; i++) out[i + 1] = out[i] + heights[i];
    return out;
  }, [heights]);

  const totalHeight = offsets[offsets.length - 1] || 0;

  // viewport = window-level scroll + grid element's top offset (so we can
  // translate window.scrollY into "pixels into the grid").
  const [viewport, setViewport] = useState({ relTop: -Infinity, viewportH: 0 });

  useEffect(() => {
    let raf = 0;
    const apply = () => {
      raf = 0;
      const el = scrollRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // How far above the visible window does the grid start?
      // relTop = scroll offset INTO the grid; can be negative if grid is still
      // below the fold.
      const relTop = -rect.top;
      setViewport({ relTop, viewportH: window.innerHeight });
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollRef, rows.length]);

  return useMemo(() => {
    // Below threshold: render everything, zero spacers.
    if (rows.length <= THRESHOLD) {
      return { visibleStart: 0, visibleEnd: rows.length, topPx: 0, bottomPx: 0, enabled: false, totalHeight };
    }
    // Translate the window viewport into "row-space" pixels.
    const top = Math.max(0, viewport.relTop);
    const bottom = top + Math.max(viewport.viewportH, 600);

    // Binary search for first row whose bottom edge > viewport top.
    let lo = 0, hi = rows.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (offsets[mid + 1] <= top) lo = mid + 1;
      else hi = mid;
    }
    let start = Math.max(0, lo - OVERSCAN);

    // Walk forward to find first row whose top edge >= viewport bottom.
    let end = start;
    while (end < rows.length && offsets[end] < bottom) end++;
    end = Math.min(rows.length, end + OVERSCAN);

    const topPx = offsets[start] || 0;
    const bottomPx = (offsets[rows.length] || 0) - (offsets[end] || 0);
    return { visibleStart: start, visibleEnd: end, topPx, bottomPx, enabled: true, totalHeight };
  }, [rows.length, viewport.relTop, viewport.viewportH, offsets, totalHeight]);
}
